import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ActivityIndicator } from 'react-native';
import { useMemo, useState } from 'react';
import { intervalToDuration } from 'date-fns';
import { Chart } from './web/chart';
import { Container } from './web/container';
import { Col, Row, TextBody, TextDisplay } from '../../atoms';
import { AmountPrice, AmountQuantity, ErrorCallout } from '../../organisms';
import { useBreakpoints } from '../../hooks';
import { AssetFundChartTimeRange } from 'common';
import { theme } from '@xbto/design-system';
import { fundGraphChartHeight } from './web/options';
export default function FundGraphWeb({ accountDetailAsset, data, header, onTimeRange, }) {
    var _a, _b, _c, _d, _e, _f;
    /**
     * State
     */
    const [selectedSeries, setSelectedSeries] = useState('asset-amount');
    /**
     * Vars
     */
    const secondaryAssetCode = (_c = (_b = (_a = accountDetailAsset === null || accountDetailAsset === void 0 ? void 0 : accountDetailAsset.fundDetails) === null || _a === void 0 ? void 0 : _a.secondaryAsset) === null || _b === void 0 ? void 0 : _b.code) !== null && _c !== void 0 ? _c : '';
    const fundInceptionDate = (_f = (_e = (_d = accountDetailAsset === null || accountDetailAsset === void 0 ? void 0 : accountDetailAsset.fundDetails) === null || _d === void 0 ? void 0 : _d.firstSubscriptionDate) === null || _e === void 0 ? void 0 : _e.toString()) !== null && _f !== void 0 ? _f : '';
    /**
     * Hooks
     */
    const { breakpoints } = useBreakpoints(500);
    const { noChart, tryAgain, strictData, isUsdFund, isLessThan1Year, loading } = useMemo(() => {
        const loading = !!(data === null || data === void 0 ? void 0 : data.loading);
        const tryAgain = !loading && !!(data === null || data === void 0 ? void 0 : data.errored);
        const noChart = !loading &&
            (data === null ||
                data.assetAmountDataPoints === null ||
                data.assetAmountDataPoints.length === 0 ||
                data.usdValueDataPoints === null ||
                data.usdValueDataPoints.length === 0);
        const strictData = data;
        const isUsdFund = (secondaryAssetCode === null || secondaryAssetCode === void 0 ? void 0 : secondaryAssetCode.toUpperCase()) === 'USD';
        const isLessThan1Year = !!fundInceptionDate &&
            intervalToDuration({
                start: new Date(fundInceptionDate),
                end: new Date(),
            }).years === 0;
        return {
            tryAgain,
            noChart,
            strictData,
            isUsdFund,
            isLessThan1Year,
            loading: !!(data === null || data === void 0 ? void 0 : data.loading),
        };
    }, [data, secondaryAssetCode, fundInceptionDate]);
    /**
     * DOM
     */
    const ErrComp = (_jsx(ErrorCallout, { title: "Unable to display holdings chart", message: "We are unable to display your holdings chart at the moment, please try again later", height: 332 }));
    if (tryAgain) {
        return ErrComp;
    }
    return (_jsxs(Col, { children: [accountDetailAsset && (_jsx(Col.L, { mb: "6", children: _jsx(Row.L, { children: _jsxs(Col, { children: [_jsx(TextBody, { small: true, children: "Your holdings" }), selectedSeries === 'asset-amount' ? (_jsxs(Row.C, { mt: "4", children: [_jsx(AmountQuantity, { as: TextDisplay, small: breakpoints.LESS_THAN_M, value: accountDetailAsset.quantity, currencyCode: secondaryAssetCode, color: "primary" }), _jsx(Col, { ml: "4", children: _jsx(AmountPrice, { color: "tertiary", value: accountDetailAsset.quantityUsd }) })] })) : (_jsxs(Row.C, { mt: "4", children: [_jsx(AmountPrice, { as: TextDisplay, small: breakpoints.LESS_THAN_M, value: accountDetailAsset.quantityUsd, color: "primary" }), _jsx(Col, { ml: "4", children: _jsx(AmountQuantity, { value: accountDetailAsset.quantity, currencyCode: secondaryAssetCode, color: "tertiary" }) })] }))] }) }) })), header && _jsx(Col, { mb: "8", children: header }), noChart ? (ErrComp) : (_jsx(Container, { accountDetailAsset: accountDetailAsset, selectedSeries: selectedSeries, timeRange: isLessThan1Year
                    ? AssetFundChartTimeRange.SINCE_INCEPTION
                    : (data === null || data === void 0 ? void 0 : data.timeRange) || AssetFundChartTimeRange.Y1, onTimeRange: onTimeRange, hideTimeRange: noChart, onSeriesToggle: setSelectedSeries, hideUsdLegend: isUsdFund, lessThan1Year: isLessThan1Year, loading: loading, children: loading ? (_jsx(ActivityIndicator, { color: theme.color.text.primary, size: 'small', style: {
                        height: fundGraphChartHeight,
                    } })) : (_jsx(Chart, { data: strictData, selectedSeries: selectedSeries, secondaryAssetCode: secondaryAssetCode, hideUsdSeries: isUsdFund, onSeriesLineSelected: setSelectedSeries })) }))] }));
}
